import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, withRouter } from 'react-router-dom';
import { PageHeader, Tabs } from 'antd';
import ConfigurationLayout from '../../../../common/layouts/ConfigurationLayout';
import Button from '../../../../common/components/Button';
import MapTabs from '../../../../common/components/MapTabs';
import ApproachesMap from '../../components/ApproachesMap';
import ApproachesGeneral from '../../components/ApproachesGeneral';
import ApproachesThresholds from '../../components/ApproachesThresholds';
import ApproachesOutputs from '../../components/ApproachesOutputs';
import useIntersection from '../../hooks/useIntersection';
import * as defaultIntersectionData from '../../data/defaultDataIntersection.json';
import openNotification from '../../../../common/components/notification';
import useMutationNotification from '../../../../common/hooks/useMutationNotification';
import {
  setIntersectionThresholds,
  setIntersectionOutputs,
  setIntersectionSaved,
  clearSelectedSegment,
} from '../../store/slice';
import './style.css';
import ConnectionErrorModal from '../../components/NoConnectionErrorModal';
import { NO_CONNECTION_ERROR_MESSAGE as UPDATE_ERROR_MESSAGE } from '../../constants';
import useUser from '../../../userAuth/hooks/useUser';
import { UserRole, UserType } from '../../../../common/enums';

let newIntersection;
const { TabPane } = Tabs;

const Pane = {
  GENERAL: 'general',
  THRESHOLDS: 'thresholds',
  OUTPUTS: 'outputs',
};

// Intersections L2 Page
const IntersectionPage = ({ match }) => {
  const { intersectionId } = match.params;

  const { userHasUserType, userHasRole } = useUser();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    intersection,
    isLoading,
    isSendingDiagCall,
    refresh,
    update,
    reset,
    sendDiagCall,
    updateIntersectionResponse,
    resetIntersectionResponse,
  } = useIntersection({ intersectionId });

  const { error: updateError } = updateIntersectionResponse;

  useMutationNotification(updateIntersectionResponse, {
    formatSuccessNotification: () => ({
      message: 'Successfully updated intersection',
    }),
    formatErrorNotification: () => ({
      message: 'Failed to update intersection',
    }),
  });

  useMutationNotification(resetIntersectionResponse, {
    formatSuccessNotification: () => ({
      message: 'Successfully deleted intersection',
    }),
    formatErrorNotification: () => ({
      message: 'Failed to deleted intersection',
    }),
    onFinish: () => history.push('/config/intersections'),
  });

  const isSaved = useSelector(
    ({ configuration }) => configuration.intersectionSaved
  );
  const generalData = useSelector(
    ({ configuration }) => configuration.intersectionGeneralData
  );
  const approachMap = useSelector(
    ({ configuration }) => configuration.intersectionApproachMap
  );
  const thresholds = useSelector(
    ({ configuration }) => configuration.intersectionThresholds
  );
  const channels = useSelector(
    ({ configuration }) => configuration.intersectionChannels
  );
  const outputs = useSelector(
    ({ configuration }) => configuration.intersectionOutputs
  );
  const channelNames = useSelector(
    ({ configuration }) => configuration.intersectionChannelNames
  );
  const ntcipConfiguration = useSelector(
    ({ configuration }) => configuration.intersectionNtcipConfiguration
  );

  const { model, communicationType } = generalData;

  const noData = useMemo(() => 
    // Even when all other fields are missing, "locationType" still gets returned with value "undefined"
    !intersection || Object.keys(intersection).length <= 1
  , [intersection]);

  useEffect(() => {
    if (!updateError) return;
    ConnectionErrorModal(
      // Allow time for the requester to flush the previous message backlog
      (i) => setTimeout(() => update(i), 3000),
      newIntersection,
      UPDATE_ERROR_MESSAGE
    );
    setIntersectionSaved(false);
  }, [update, updateError]);

  const onSave = useCallback(() => {
    newIntersection = {
      ...intersection,
      ...generalData,
      ntcipConfiguration,
      approachMap,
      thresholds,
      channels,
      outputs,
    };

    const erroredApproach = approachMap.find((value) => value.isError);

    if (erroredApproach) {
      openNotification({
        message: 'Approach Map Error',
        description: erroredApproach.error,
      });
      return;
    }

    dispatch(setIntersectionSaved(true));
    dispatch(clearSelectedSegment());
    update(newIntersection);
  }, [
    intersection,
    generalData,
    approachMap,
    thresholds,
    channels,
    ntcipConfiguration,
    outputs,
    dispatch,
    update,
  ]);

  const onCancel = useCallback(() => {
    refresh();
    dispatch(setIntersectionSaved(true));
  }, [dispatch, refresh]);

  const resetThresholdsToDefaults = useCallback(
    (priority) => {
      const { thresholds: defaultThresholds } = defaultIntersectionData.default;

      if (priority === 'Low') {
        dispatch(
          setIntersectionThresholds({
            ...thresholds,
            lowPriority: defaultThresholds.lowPriority,
          })
        );
      }
      if (priority === 'High') {
        dispatch(
          setIntersectionThresholds({
            ...thresholds,
            highPriority: defaultThresholds.highPriority,
          })
        );
      }
      dispatch(setIntersectionSaved(false));
    },
    [dispatch, thresholds]
  );

  const resetOutputsToDefaults = useCallback(
    (priority) => {
      const { outputs: defaultOutputs } = defaultIntersectionData.default;

      if (priority === 'Low') {
        dispatch(
          setIntersectionOutputs({
            ...outputs,
            lowPriority: defaultOutputs.lowPriority,
          })
        );
      }
      if (priority === 'High') {
        dispatch(
          setIntersectionOutputs({
            ...outputs,
            highPriority: defaultOutputs.highPriority,
          })
        );
      }
      dispatch(setIntersectionSaved(false));
    },
    [dispatch, outputs]
  );

  const handleBack = useCallback(() => {
    history.push('/config/intersections');
  }, [history]);

  const isEditor = userHasRole([UserRole.ORG_ADMIN, UserRole.READ_WRITE]);

  return (
    <ConfigurationLayout>
      <div
        className={`approach-container approach-container-is-saved-${!isEditor}`}
      >
        {isEditor && (
          <PageHeader
            onBack={handleBack}
            title="Now Editing Intersection"
            extra={
              <>
                {!isLoading && userHasUserType(UserType.MioAdmin) && (
                  <Button
                    key="3"
                    type="danger"
                    onClick={() => reset(intersection)}
                    disabled={noData}
                  >
                    Delete
                  </Button>
                )}
                {!isSaved && isEditor && (
                  <Button key="2" type="secondary" onClick={onCancel}>
                    Cancel
                  </Button>
                )}
                {!isSaved && isEditor && (
                  <Button key="1" onClick={onSave}>
                    Save
                  </Button>
                )}
              </>
            }
          />
        )}
        <MapTabs
          defaultActiveKey={Pane.GENERAL}
          tabs={
            <>
              <TabPane tab="General" key={Pane.GENERAL} disabled={noData}>
                <ApproachesGeneral
                  generalData={generalData}
                  ntcipConfiguration={ntcipConfiguration}
                  channelNames={channelNames}
                />
              </TabPane>
              <TabPane tab="Thresholds" key={Pane.THRESHOLDS} disabled={noData}>
                <ApproachesThresholds
                  thresholds={thresholds}
                  channelNames={channelNames}
                  resetToDefaults={resetThresholdsToDefaults}
                />
              </TabPane>
              <TabPane tab="Outputs" key={Pane.OUTPUTS} disabled={noData}>
                <ApproachesOutputs
                  model={model}
                  communicationType={communicationType}
                  outputs={outputs}
                  channelNames={channelNames}
                  sendTestCall={sendDiagCall}
                  isSendingTestCall={isSendingDiagCall}
                  resetToDefaults={resetOutputsToDefaults}
                />
              </TabPane>
            </>
          }
          tabsLoading={isLoading}
          map={
            <ApproachesMap
              isLoading={isLoading}
              noData={noData}
              lat={isLoading ? 0 : intersection.latitude}
              lng={isLoading ? 0 : intersection.longitude}
              approachMap={approachMap}
            />
          }
        />
      </div>
    </ConfigurationLayout>
  );
};

export default withRouter(IntersectionPage);
